import { useEffect, useMemo, useState } from "react";
import { Pencil, Users } from "lucide-react";

import { Button } from "components/ui/Button";
import { CardsSection } from "components/layout/Section/CardsSection";
import { atelierRolesAPI } from "services/swagger-api";
import { GetProfilsResponse } from "api/atelier-roles";

import { CORPORATE_ROLE_OPTIONS } from "../form/plateformeRoles";
import { estAdministree as estModeAdministree } from "../modeGestion";
import { CorporateRolesEditor } from "./CorporateRolesEditor";

interface CorporateRolesCardProps {
  plateformeId: string;
  assignedRoles: string[];
  /** Suppléments réellement attribuables à cette PM (liste blanche ∩ garde de
   * nature, servie par le back). */
  assignableRoles: string[];
  /** Codes nature / sous-nature de la PM — résolvent son profil de briques. */
  nature?: string | null;
  sousNature?: string | null;
  /** Mode de gestion de la plateforme (GEREE / ADMINISTREE). */
  modeGestion?: string;
  onSaved: () => void;
}

// Référentiel statique de l'Atelier des rôles, partagé au niveau module :
// l'onglet Gouvernance est monté/démonté à chaque bascule d'onglet, inutile de
// retélécharger le catalogue à chaque montage. Un échec n'est pas mis en cache.
let profilsPromise: Promise<GetProfilsResponse> | null = null;
const loadProfils = (): Promise<GetProfilsResponse> => {
  profilsPromise ??= atelierRolesAPI.getProfils().catch((error) => {
    profilsPromise = null;
    throw error;
  });
  return profilsPromise;
};

const Chip: React.FC<{ label: string; tone?: "auto" | "manual" }> = ({ label, tone = "manual" }) => (
  <span
    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
      tone === "auto" ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-700"
    }`}
  >
    {label}
  </span>
);

/**
 * L'état COMPLET des rôles et briques du compte de la PM, avec leur provenance :
 * le kit du profil (nature/sous-nature, automatique pour chaque membre), la
 * brique acquise par la gouvernance (distribution collective), et les
 * suppléments — seule partie modifiable ici.
 */
export const CorporateRolesCard: React.FC<CorporateRolesCardProps> = ({
  plateformeId,
  assignedRoles,
  assignableRoles,
  nature,
  sousNature,
  modeGestion,
  onSaved,
}) => {
  const [editing, setEditing] = useState(false);
  const [profils, setProfils] = useState<GetProfilsResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadProfils()
      .then((response) => {
        if (!cancelled) setProfils(response);
      })
      // Best-effort : sans le référentiel, la carte vit avec les seuls suppléments.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Profil du cabinet : mapping (nature, sous-nature) → profil, repli sur le
  // variant par défaut de la nature (sous-nature "").
  const profilDuCabinet = useMemo(() => {
    if (!profils || !nature) return null;
    const mapping =
      profils.natures.find((n) => n.nature === nature && n.sousNature === (sousNature ?? "")) ??
      profils.natures.find((n) => n.nature === nature && n.sousNature === "");
    if (!mapping?.profilId) return null;
    return profils.profils.find((p) => p.id === mapping.profilId) ?? null;
  }, [profils, nature, sousNature]);

  const estAdministree = estModeAdministree(modeGestion);

  if (editing) {
    return (
      <CorporateRolesEditor
        plateformeId={plateformeId}
        assignedRoles={assignedRoles}
        assignableRoles={assignableRoles}
        onSaved={() => {
          setEditing(false);
          onSaved();
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const assignedLabels = CORPORATE_ROLE_OPTIONS.filter((o) =>
    assignedRoles.includes(o.value),
  ).map((o) => o.label);

  return (
    <CardsSection
      icon={<Users className="w-5 h-5 text-primary" />}
      title="Rôles et briques"
      titleAside={
        <Button outline color="primary" onClick={() => setEditing(true)}>
          <Pencil data-slot="icon" />
          Modifier
        </Button>
      }
    >
      <p className="-mt-2 text-sm text-gray-500">
        Ce que porte le compte de la personne morale, et d&apos;où ça vient.
      </p>
      <div className="rounded-2xl border bg-white p-6 flex flex-col gap-5">
        {profilDuCabinet && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Briques du profil « {profilDuCabinet.nom} »
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {profilDuCabinet.roles.map((role) => (
                <Chip key={role} label={role} tone="auto" />
              ))}
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              Attribuées automatiquement à chaque membre, selon la nature du cabinet
              (configurable dans l&apos;Atelier des rôles).
            </p>
          </div>
        )}

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Gouvernance
          </p>
          {estAdministree ? (
            <p className="mt-2 text-sm text-gray-500">
              Plateforme administrée par l&apos;admin : aucune brique de gouvernance
              n&apos;est distribuée au titulaire.
            </p>
          ) : (
            <>
              <div className="mt-2 flex flex-wrap gap-2">
                <Chip label="Consultation portefeuille orga" tone="auto" />
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                Acquise par la gouvernance de la plateforme — distribuée à tous les
                membres actifs, non modifiable ici.
              </p>
            </>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Suppléments
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {assignedLabels.map((label) => (
              <Chip key={label} label={label} />
            ))}
          </div>
          {assignedLabels.length === 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Aucun supplément attribué (rôles de gestion, conception d&apos;offre,
              consultation porteur de risque).
            </p>
          )}
        </div>
      </div>
    </CardsSection>
  );
};
